import React, { useEffect, useRef } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import { withViewport } from '../../util/uiHelpers';
import { ensureCurrentUser, ensureUser } from '../../util/data';
import { FormattedMessage, injectIntl } from '../../util/reactIntl';
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { LayoutSingleColumn, Page } from '../../components';

import NotFoundPage from '../NotFoundPage/NotFoundPage';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import Profile from './Profile/Profile';
import SearchSection from './SearchSection/SearchSection';

import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/ui.duck';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { updateRecordsOrder } from './StoreProfilePage.duck';

import MobileBottomNavbar from '../../components/MobileBottomNavbar/MobileBottomNavbar';
import { fetchTimeSlots, fetchTransactionLineItems } from '../ListingPage/ListingPage.duck';
import { addToCart } from '../../ducks/cart.duck';
import { types as sdkTypes } from 'sharetribe-flex-sdk';
const { Money, UUID } = sdkTypes;
import css from './StoreProfilePage.module.css';


export const transformItemsToOriginalListings = items => {
  return items.reduce((listings, item) => {
    const { _geoloc, price, ...rest } = item;
    if (price && price.amount && price.currency) {
      const listing = {
        id: new UUID(rest.objectID),
        type: 'listing',
        attributes: {
          geolocation: { ..._geoloc },
          price: new Money(price.amount, price.currency),
          ...rest,
        },
      };
      return [...listings, listing];
    } else {
      return listings;
    }
  }, []);
};

const getAuthorImageVariant = (authorImage, variantName) => {
  const { variants = {} } = authorImage?.attributes || {};
  return variants[variantName];
};

const MainContent = props => {
  const mainContentRef = useRef(null);
  const { userShowError } = props;

  useEffect(() => {
    if (typeof window != 'undefined') {
      const offsetPosition = -200;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  }, []);

  if (userShowError) {
    return (
      <p className={css.error}>
        <FormattedMessage id="StoreProfilePage.loadingDataFailed" />
      </p>
    );
  }

  return (
    <div className={css.mainContent} ref={mainContentRef}>
      <Profile {...props} />
      <SearchSection {...props} />
    </div>
  );
};

function StoreProfilePageComponent(props) {
  const config = useConfiguration();
  const routes = useRouteConfiguration();
  const {
    intl,
    location,
    user,
    history,
    currentUser,
    userShowError,
    userShowInProgress,
    scrollingDisabled,
  } = props;

  const ensuredCurrentUser = ensureCurrentUser(currentUser);
  const profileUser = ensureUser(user);

  const isCurrentUser =
    ensuredCurrentUser.id &&
    profileUser.id &&
    ensuredCurrentUser.id.uuid === profileUser.id.uuid;
  const { bio = '', displayName, publicData = {} } =
    (profileUser && profileUser.id && profileUser.attributes.profile) || {};

  const { seoTitle, seoDescription, storeName, city } = publicData;

  if (userShowError && userShowError.status === 404) {
    // 404 profile not found
    return <NotFoundPage />;
  } else if (userShowError) {
    // Other error in fetching profile
    return (
      <Page
        title={intl.formatMessage({
          id: 'StoreProfilePage.errorLoadingProfileTitle',
        })}
        scrollingDisabled={scrollingDisabled}
      >
        <LayoutSingleColumn
          topbar={<TopbarContainer />}
          footer={<FooterContainer />}
          mobileBottomNavbar={<MobileBottomNavbar />}
        >
          <p className={css.loadingText}>
            <FormattedMessage id="StoreProfilePage.errorLoadingProfileMessage" />
          </p>
        </LayoutSingleColumn>
      </Page>
    );
  } else if (userShowInProgress || (!user || (user && !user.id))) {
    // Still loading the Profile
    return (
      <Page
        title={intl.formatMessage({
          id: 'StoreProfilePage.loadingProfileTitle',
        })}
        scrollingDisabled={scrollingDisabled}
      >
        <LayoutSingleColumn
          topbar={<TopbarContainer />}
          footer={<FooterContainer />}
          mobileBottomNavbar={<MobileBottomNavbar />}
        >
          <p className={css.loadingText}>
            <FormattedMessage id="StoreProfilePage.loadingProfileMessage" />
          </p>
        </LayoutSingleColumn>
      </Page>
    );
  }

  const schemaTitleVars = {
    name: displayName,
    marketplaceName: config.marketplaceName,
  };

  const schemaTitle = intl.formatMessage(
    { id: 'StoreProfilePage.schemaTitle' },
    schemaTitleVars
  );

  // const facebookImage = getAuthorImageVariant(profileUser.profileImage, 'facebook');
  // const twitterImage = getAuthorImageVariant(profileUser.profileImage, 'twitter');
  const schemaImage = getAuthorImageVariant(
    profileUser.profileImage,
    'scaled-small'
  );
  const storeUrl = `${config.marketplaceRootURL}${location.pathname}${location.search}${location.hash}`;

  const facebookImages = getAuthorImageVariant(
    profileUser.profileImage,
    'facebook'
  );
  const twitterImages = getAuthorImageVariant(
    profileUser.profileImage,
    'twitter'
  );

  const defaultStoreName = storeName ? storeName : displayName;
  const defaultSeoTitle = intl.formatMessage({ id: 'ProfileSettingsPage.defaultSEOTitle' }, { storeName: defaultStoreName, city });
  const defaultSeoDescription = intl.formatMessage({ id: 'ProfileSettingsPage.defaultSEODescription' }, { storeName: defaultStoreName, city });

  return (
    <Page
      title={seoTitle || defaultSeoTitle}
      scrollingDisabled={scrollingDisabled}
      author={displayName}
      description={seoDescription || defaultSeoDescription}
      facebookImages={!!facebookImages ? [facebookImages] : null}
      twitterImages={!!twitterImages ? [twitterImages] : null}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'ProfilePage',
        description: bio,
        name: schemaTitle,
        image: schemaImage,
      }}
    >
      <LayoutSingleColumn
        className={css.pageRoot}
        topbar={<TopbarContainer />}
        footer={<FooterContainer />}
        mobileBottomNavbar={<MobileBottomNavbar />}
      >
        <MainContent
          user={profileUser}
          storeUrl={storeUrl}
          isCurrentUser={isCurrentUser}
          routes={routes}
          history={history}
          config={config}
          {...props}
        />
      </LayoutSingleColumn>
    </Page>
  );
}

const mapStateToProps = state => {
  const { currentUser, currentUserCurrency } = state.user;

  const {
    userId,
    userShowError,
    userShowInProgress,
    reviews,
    totalReviews,
    queryReviewsError,
    userListingRefs,
    totalListings,
    queryListingsError,
    updateOrderError,
    isVerified
  } = state.StoreProfilePage;

  const {
    exchangeRates,
    fetRatesError,
    fetchRatesInProgress,
  } = state.exchangeRate;

  const userMatches = getMarketplaceEntities(state, [
    { type: 'user', id: userId },
  ]);
  const user = userMatches.length === 1 ? userMatches[0] : null;

  return {
    user,
    userId,
    currentUser,
    userShowError,
    userShowInProgress,
    reviews,
    totalReviews,
    queryReviewsError,
    userListingRefs,
    totalListings,
    queryListingsError,
    scrollingDisabled: isScrollingDisabled(state),
    updateOrderError,
    isVerified,
    exchangeRates,
    fetRatesError,
    fetchRatesInProgress,
    currentUserCurrency
  };
};

const mapDispatchToProps = dispatch => ({
  onUpdateRecordsOrder: data => dispatch(updateRecordsOrder(data)),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onFetchTransactionLineItems: params =>
    dispatch(fetchTransactionLineItems(params)),
  onFetchTimeSlots: (listingId, start, end, timeZone) =>
    dispatch(fetchTimeSlots(listingId, start, end, timeZone)),
  onAddToCart: (config, params, queryID) =>
    dispatch(addToCart(config, params, queryID)),
});

const StoreProfilePage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  withViewport,
  injectIntl,
  withRouter
)(StoreProfilePageComponent);

export default StoreProfilePage;
